import React, { useState } from 'react';
import { FlatList, Pressable, View } from 'react-native';

import { DateDisplay } from '@/components/common/date-display';
import { OwnerHeader } from '@/components/common/owner-header';
import { ActivityIndicator, Text, useTheme } from '@/components/ui';
import type { ExteriorApplicationDetail } from '@/services/data-service';
import { decisionLine } from '@/utils/application-decision';
import { APPLICATION_STAGE_LABEL, PENDING_US_STAGES } from '@/utils/application-stage';
import { applicationStatusStyle } from '@/utils/application-status-style';

/**
 * The middle column: improvement REQUESTS, in the shape the message list uses for mail.
 *
 * Same three-pane muscle memory as the inbox — pick a scope on the left, scan the list,
 * open one on the right — but the unit here is the request, not the individual email. A
 * request gathers several messages, and a reviewer works the request.
 */
export interface ApplicationListProps {
	applications: ExteriorApplicationDetail[];
	selectedId?: string | null;
	onSelect: (application: ExteriorApplicationDetail) => void;
	loading?: boolean;
	emptyText?: string;
	/**
	 * This list is INSIDE another scroller, so it must not scroll or stretch.
	 *
	 * The `flex: 1` below is load-bearing when the list owns its pane, and actively wrong
	 * when a page that already scrolls renders it as one section of several — it claims the
	 * whole viewport and pushes the next section past the fold.
	 */
	embedded?: boolean;
	/** Drawn after the last row, inside the scroller — e.g. a paged list's "Load more". */
	footer?: React.JSX.Element | null;
}

const StatusChip: React.FC<{ status: number; label: string }> = ({ status, label }) => {
	const style = applicationStatusStyle(status);
	return (
		<View style={{ paddingHorizontal: 8, paddingVertical: 2, borderRadius: 10, backgroundColor: style.backgroundColor }}>
			<Text style={{ fontSize: 11, fontWeight: '700', color: style.color }}>{label}</Text>
		</View>
	);
};

const Row: React.FC<{
	application: ExteriorApplicationDetail;
	selected: boolean;
	onPress: () => void;
}> = ({ application, selected, onPress }) => {
	const theme = useTheme();
	const [hovered, setHovered] = useState(false);
	const unread = (application.unreadCount ?? 0) > 0;
	// "Approved 10/3/2026 by Sue Kwadrat" — who decided, on the Eastern business day (#611).
	const decision = decisionLine(application);
	return (
		<Pressable
			onPress={onPress}
			onHoverIn={() => setHovered(true)}
			onHoverOut={() => setHovered(false)}
			testID={`application-row-${application.id}`}
			style={{
				paddingVertical: 10, paddingHorizontal: 14,
				borderBottomWidth: 1,
				borderBottomColor: theme.colors.outlineVariant ?? theme.colors.outline,
				backgroundColor: selected
					? theme.colors.primaryContainer
					: hovered ? theme.colors.surfaceVariant : 'transparent',
			}}
		>
			{/* Same header the inbox rows use — see components/common/owner-header. */}
			<OwnerHeader
				testID={`application-row-${application.id}-owner`}
				associationName={application.associationName}
				managerId={application.managerId}
				managerName={application.managerName}
				managerColor={application.managerColor}
			/>
			<View style={{ flexDirection: 'row', alignItems: 'center' }}>
				<Text
					numberOfLines={1}
					style={{ flex: 1, minWidth: 0, fontWeight: unread ? '700' : '600', color: theme.colors.onSurface }}
				>
					{application.applicationNumber != null ? `#${application.applicationNumber} · ` : ''}
					{application.accountOwnerName || application.submitterName || 'Unknown owner'}
				</Text>
				{application.createdAt ? (
					<DateDisplay
						date={new Date(application.createdAt)}
						dateOnly
						variant="bodySmall"
						style={{ color: theme.colors.onSurfaceVariant, marginLeft: 8 }}
					/>
				) : null}
			</View>
			<Text numberOfLines={1} style={{ marginTop: 2, color: theme.colors.onSurface }}>
				{application.requestTypeName || 'Uncategorized'}
			</Text>
			<Text numberOfLines={1} style={{ marginTop: 2, fontSize: 12, color: theme.colors.onSurfaceVariant }}>
				{application.accountNumber ? `${application.accountNumber} · ` : ''}
				{application.propertyAddress || application.associationName || '—'}
			</Text>
			{decision ? (
				<Text
					numberOfLines={1}
					testID={`application-row-${application.id}-decision`}
					style={{ marginTop: 2, fontSize: 12, color: theme.colors.onSurfaceVariant }}
				>
					{decision}
				</Text>
			) : null}
			<View style={{ flexDirection: 'row', alignItems: 'center', gap: 6, marginTop: 6 }}>
				<StatusChip status={application.status} label={application.statusLabel} />
				{/* The stage, not the status: "is this on my desk?" is the question a
				    reviewer scanning this list is actually asking. Only drawn when the
				    answer is yes, so the row stays quiet once the ball is elsewhere. */}
				{PENDING_US_STAGES.includes(application.stage) ? (
					<View style={{ paddingHorizontal: 8, paddingVertical: 2, borderRadius: 10, backgroundColor: theme.colors.errorContainer ?? theme.colors.error }}>
						<Text style={{ fontSize: 11, fontWeight: '700', color: theme.colors.onErrorContainer ?? theme.colors.onPrimary }}>
							{APPLICATION_STAGE_LABEL[application.stage]}
						</Text>
					</View>
				) : null}
				{/* Unread is the "needs a human" signal — a request with new mail on it has
				    moved without anyone looking. */}
				{unread ? (
					<View style={{ paddingHorizontal: 8, paddingVertical: 2, borderRadius: 10, backgroundColor: theme.colors.primary }}>
						<Text style={{ fontSize: 11, fontWeight: '700', color: theme.colors.onPrimary }}>
							{application.unreadCount} new
						</Text>
					</View>
				) : null}
			</View>
		</Pressable>
	);
};

export const ApplicationList: React.FC<ApplicationListProps> = ({
	applications, selectedId, onSelect, loading = false, emptyText = 'No requests here.', embedded = false, footer,
}) => {
	const theme = useTheme();
	if (loading && applications.length === 0) {
		return <View style={{ flex: embedded ? undefined : 1, alignItems: 'center', paddingTop: 40 }}><ActivityIndicator /></View>;
	}
	if (applications.length === 0) {
		return (
			<View style={{ flex: embedded ? undefined : 1, alignItems: 'center', paddingTop: 40 }}>
				<Text style={{ color: theme.colors.onSurfaceVariant, fontStyle: 'italic' }}>{emptyText}</Text>
				{/* A search can match nothing on the loaded pages and still match a later one. */}
				{footer}
			</View>
		);
	}
	return (
		<FlatList
			testID="application-list"
			// flex:1 is load-bearing on RN-Web — without it the list collapses to zero height.
			// Embedded, the enclosing ScrollView provides the height and scrolling instead, so
			// the list must size to its content and keep its hands off the axis.
			style={embedded ? undefined : { flex: 1 }}
			scrollEnabled={!embedded}
			data={applications}
			ListFooterComponent={footer ?? undefined}
			keyExtractor={(item) => item.id}
			renderItem={({ item }) => (
				<Row application={item} selected={item.id === selectedId} onPress={() => onSelect(item)} />
			)}
		/>
	);
};

export default ApplicationList;
